import { useEffect, useState } from "react";

export type VoiceDockMeterSpeaker = "idle" | "user" | "agent";

export interface TopMeterCell {
  key: string;
  column: number;
  row: number;
  lit: boolean;
  intensity: number;
}

export interface VoiceDockMeterState {
  volume: number;
  speaker: VoiceDockMeterSpeaker;
  phase: number;
}

export function useVoiceDockMeter({
  userEnabled,
  agentEnabled,
  responseMode,
}: {
  userEnabled: boolean;
  agentEnabled: boolean;
  responseMode: number;
}): VoiceDockMeterState {
  const [meter, setMeter] = useState<VoiceDockMeterState>({
    volume: 0,
    speaker: "idle",
    phase: 0,
  });

  useEffect(() => {
    let frame = 0;
    let userVolume = 0;
    let userVolumeAt = 0;
    let agentVolume = 0;
    let agentVolumeAt = 0;

    const handleUserVolume = (event: Event) => {
      const detail = (event as CustomEvent<{ volume?: number }>).detail;
      if (typeof detail?.volume !== "number") return;
      userVolume = clamp(detail.volume);
      userVolumeAt = window.performance.now();
    };
    const handleAgentVolume = (event: Event) => {
      const detail = (event as CustomEvent<{ volume?: number }>).detail;
      if (typeof detail?.volume !== "number") return;
      agentVolume = clamp(detail.volume);
      agentVolumeAt = window.performance.now();
    };

    const interval = window.setInterval(() => {
      const now = window.performance.now();
      const hasUserVolume = now - userVolumeAt < 260;
      const hasAgentVolume = now - agentVolumeAt < 260;
      const hasLiveAgentVolume = hasAgentVolume && agentVolume > 0.02;
      const next =
        agentEnabled || hasLiveAgentVolume
          ? {
              volume: hasAgentVolume
                ? agentVolume
                : sampleAgentVolume(frame, responseMode),
              speaker: "agent" as const,
            }
          : userEnabled
            ? {
                volume: hasUserVolume ? userVolume : 0.08,
                speaker: "user" as const,
              }
            : { volume: 0, speaker: "idle" as const };

      setMeter({
        volume: Number(next.volume.toFixed(2)),
        speaker: next.speaker,
        phase: frame,
      });
      frame += 1;
    }, 72);

    window.addEventListener("fintheon:voice-cao-user-volume", handleUserVolume);
    window.addEventListener(
      "fintheon:voice-cao-agent-volume",
      handleAgentVolume,
    );
    return () => {
      window.clearInterval(interval);
      window.removeEventListener(
        "fintheon:voice-cao-user-volume",
        handleUserVolume,
      );
      window.removeEventListener(
        "fintheon:voice-cao-agent-volume",
        handleAgentVolume,
      );
    };
  }, [agentEnabled, responseMode, userEnabled]);

  return meter;
}

export function buildTopMeterCells(
  volume: number,
  phase = 0,
  speaker: VoiceDockMeterSpeaker = "idle",
): TopMeterCell[] {
  const rowCount = 5;
  const isLive = speaker !== "idle";

  return topMeterColumnWeights.flatMap((weight, column) => {
    const pulse =
      isLive && volume > 0.11
        ? Math.sin(column * 0.72 + phase * 0.34) * 0.07
        : 0;
    const columnVolume = clamp(volume * (0.5 + weight * 0.66) + pulse);
    const ambientHeight = isLive ? 1 : 0;
    const height = Math.max(
      ambientHeight,
      Math.min(rowCount, Math.ceil(columnVolume * rowCount)),
    );

    return Array.from({ length: rowCount }, (_, row) => ({
      key: `${column}-${row}`,
      column,
      row,
      lit: row >= rowCount - height,
      intensity: columnVolume,
    }));
  });
}

export function useAgentReplyVolume({
  enabled,
  responseMode,
}: {
  enabled: boolean;
  responseMode: number;
}) {
  const [volume, setVolume] = useState(0);

  useEffect(() => {
    let frame = 0;
    let externalVolume = 0;
    let externalVolumeAt = 0;

    const handleVolume = (event: Event) => {
      const detail = (event as CustomEvent<{ volume?: number }>).detail;
      if (typeof detail?.volume !== "number") return;
      externalVolume = clamp(detail.volume);
      externalVolumeAt = window.performance.now();
    };

    const interval = window.setInterval(() => {
      const now = window.performance.now();
      const hasExternalVolume = now - externalVolumeAt < 260;
      const nextVolume = enabled
        ? hasExternalVolume
          ? externalVolume
          : sampleAgentVolume(frame, responseMode)
        : 0;
      setVolume(Number(nextVolume.toFixed(2)));
      frame += 1;
    }, 72);

    window.addEventListener("fintheon:voice-cao-agent-volume", handleVolume);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener(
        "fintheon:voice-cao-agent-volume",
        handleVolume,
      );
    };
  }, [enabled, responseMode]);

  return volume;
}

export function buildTopWaveRibbonPath(volume: number, phase: number): string {
  const upper = topWaveCoordinates(volume, phase, -1);
  const lower = topWaveCoordinates(volume, phase, 1).reverse();
  const first = upper[0];
  if (!first) return "";
  const upperPath = upper
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
    .join(" ");
  const lowerPath = lower.map((point) => `L ${point.x} ${point.y}`).join(" ");
  return `${upperPath} ${lowerPath} Z`;
}

export function buildTopWaveTracePath(volume: number, phase: number): string {
  return topWaveCoordinates(volume, phase, 0)
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
    .join(" ");
}

const agentVolumeSamples = [
  0.18, 0.42, 0.71, 0.54, 0.29, 0.62, 0.86, 0.47, 0.34, 0.75, 0.58, 0.22, 0.49,
  0.82, 0.66, 0.38, 0.7, 0.44,
];

const agentVolumeEnergyByResponse = [0.94, 0.72, 0.82, 0.58];

const topMeterColumnWeights = [
  0.36, 0.44, 0.56, 0.5, 0.68, 0.58, 0.74, 0.82, 0.64, 0.48, 0.78, 0.9, 0.72,
  0.6, 0.86, 1, 0.76, 0.58, 0.92, 0.8, 0.66, 0.94, 0.74, 0.62, 0.88, 0.7, 0.54,
  0.82, 0.64, 0.46, 0.72, 0.58, 0.5, 0.66, 0.48, 0.4,
];

function sampleAgentVolume(frame: number, responseMode: number) {
  const sample =
    agentVolumeSamples[
      (frame + responseMode * 5) % agentVolumeSamples.length
    ] ?? 0;
  const phraseEnergy =
    agentVolumeEnergyByResponse[
      responseMode % agentVolumeEnergyByResponse.length
    ] ?? 0.72;
  const breathGate = frame % 13 === 12 ? 0.38 : 1;
  return clamp(sample * phraseEnergy * breathGate);
}

function topWaveCoordinates(volume: number, phase: number, mirror: -1 | 0 | 1) {
  const amplitude = Math.max(1.8, clamp(volume) * 13.6);
  const pointCount = 32;
  return Array.from({ length: pointCount }, (_, index) => {
    const x = (index / (pointCount - 1)) * 100;
    const weight = topMeterColumnWeights[index % topMeterColumnWeights.length];
    const wave =
      Math.sin(index * 0.76 + phase * 0.28) * amplitude * weight +
      Math.sin(index * 1.38 + phase * 0.16) * amplitude * 0.24;
    const y = mirror === 0 ? 18 - wave * 0.2 : 18 + wave * mirror;
    return {
      x: x.toFixed(2),
      y: y.toFixed(2),
    };
  });
}

function clamp(value: number) {
  return Math.max(0, Math.min(1, value));
}
